import {
  OptimizationActionType,
  OptimizationAddonAdjustmentType,
  OptimizationAddonScope,
  OptimizationPlanScope,
  OptimizationPriceAdjustmentType,
  OptimizationSliceHelper,
  type OptimizationAction,
  type OptimizerChargebeePlan,
} from '@bbkAdminRedux/rtkq/optimizations.slice';

const MIN_API_FRACTION_DIGITS = 0;
const MAX_API_FRACTION_DIGITS = 10;

/** Null/undefined plan `decimal_places` → 1 fraction digit; otherwise clamp to a safe range. */
const planFractionDigitsFromDecimalPlaces = (
  decimalPlaces: number | null | undefined
): number => {
  if (decimalPlaces === null || decimalPlaces === undefined) {
    return 1;
  }
  if (!Number.isFinite(decimalPlaces)) {
    return 1;
  }
  const n = Math.trunc(decimalPlaces);
  return Math.min(
    MAX_API_FRACTION_DIGITS,
    Math.max(MIN_API_FRACTION_DIGITS, n)
  );
};

/** Max fraction digits from plans that share the same currency as the addon. */
export const maxFractionDigitsForCurrency = (
  plans: OptimizerChargebeePlan[],
  currencyCode: string | undefined
): number => {
  const cur = currencyCode?.trim() ?? '';
  if (!cur) {
    return 1;
  }
  const matching = plans.filter(
    (p) => p.currency_code?.trim().toUpperCase() === cur.toUpperCase()
  );
  if (matching.length === 0) {
    return 1;
  }
  return Math.max(
    ...matching.map((p) =>
      planFractionDigitsFromDecimalPlaces(p.decimal_places)
    )
  );
};

/**
 * Max fraction digits needed for a single amount that applies across all
 * subscription currencies: max over each distinct plan catalog currency of
 * {@link maxFractionDigitsForCurrency}.
 */
export const maxFractionDigitsAcrossPlanCurrencies = (
  plans: OptimizerChargebeePlan[]
): number => {
  if (plans.length === 0) {
    return 1;
  }
  const seen = new Set<string>();
  const distinctCurrencies: string[] = [];
  for (const p of plans) {
    const raw = p.currency_code?.trim() ?? '';
    if (!raw) {
      continue;
    }
    const key = raw.toUpperCase();
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    distinctCurrencies.push(raw);
  }
  if (distinctCurrencies.length === 0) {
    return 1;
  }
  return Math.max(
    ...distinctCurrencies.map((c) => maxFractionDigitsForCurrency(plans, c))
  );
};

/** Minimum fraction digits for Price Optimizer fixed-amount inputs (plan/addon), regardless of billing-site multi-decimal setting. */
export const MIN_FIXED_AMOUNT_ADJUSTMENT_FRACTION_DIGITS = 2;

/**
 * Catalog-derived max fraction digits, clamped so fixed-amount adjustment fields
 * always allow at least {@link MIN_FIXED_AMOUNT_ADJUSTMENT_FRACTION_DIGITS}.
 */
export const fractionDigitsForFixedAmountAdjustmentInput = (
  catalogBasedMax: number
): number => {
  if (!Number.isFinite(catalogBasedMax)) {
    return MIN_FIXED_AMOUNT_ADJUSTMENT_FRACTION_DIGITS;
  }
  return Math.min(
    MAX_API_FRACTION_DIGITS,
    Math.max(MIN_FIXED_AMOUNT_ADJUSTMENT_FRACTION_DIGITS, catalogBasedMax)
  );
};

export const fixedAmountAdjustmentFractionDigitsAcrossPlanCurrencies = (
  plans: OptimizerChargebeePlan[]
): number =>
  fractionDigitsForFixedAmountAdjustmentInput(
    maxFractionDigitsAcrossPlanCurrencies(plans)
  );

export const fixedAmountAdjustmentFractionDigitsForCurrency = (
  plans: OptimizerChargebeePlan[],
  currencyCode: string | undefined
): number =>
  fractionDigitsForFixedAmountAdjustmentInput(
    maxFractionDigitsForCurrency(plans, currencyCode)
  );

/**
 * Parses a fixed-amount text field and rounds to the given fraction digit count.
 * Empty, ".", invalid, or negative input yields 0.
 */
export const parseAndRoundNonNegativeFixedAmount = (
  raw: string,
  maxFractionDigits: number
): number => {
  const trimmed = raw.trim();
  if (trimmed === '' || trimmed === '.') {
    return 0;
  }
  const parsed = parseFloat(trimmed.replace(/,/g, ''));
  if (Number.isNaN(parsed) || parsed < 0) {
    return 0;
  }
  return Number(parsed.toFixed(maxFractionDigits));
};

type PlanPriceAdjustmentAction = {
  display_name: string;
  condition: OptimizationAction['condition'];
  type: OptimizationActionType.PLAN_PRICE_ADJUSTMENT;
  scope?: OptimizationPlanScope;
  adjustment_type?: OptimizationPriceAdjustmentType;
  adjustment_value?: number;
  plan_adjustments?: Array<{
    plan_id: string;
    adjustment_type: OptimizationPriceAdjustmentType;
    adjustment_value: number;
  }>;
};

type AddonPriceAdjustmentAction = {
  display_name: string;
  condition: OptimizationAction['condition'];
  type: OptimizationActionType.ADDON_PRICE_ADJUSTMENT;
  scope?: OptimizationAddonScope;
  adjustment_type?: OptimizationAddonAdjustmentType;
  adjustment_value?: number;
  addons?: Array<{
    item_price_id: string;
    adjustment_type: OptimizationAddonAdjustmentType;
    adjustment_value: number;
  }>;
};

type ActionChangePrompt = {
  title: string;
  description: string;
  confirmLabel: string;
};

export type PendingActionChange = {
  nextAction: OptimizationAction;
  prompt: ActionChangePrompt;
};

type PlanScalarAdjustment = {
  adjustment_type: OptimizationPriceAdjustmentType;
  adjustment_value: number;
};

type AddonScalarAdjustment = {
  adjustment_type: OptimizationAddonAdjustmentType;
  adjustment_value: number;
};

const DEFAULT_PLAN_ADJUSTMENT: PlanScalarAdjustment = {
  adjustment_type: OptimizationPriceAdjustmentType.INCREASE_BY_PERCENTAGE,
  adjustment_value: 10,
};

const DEFAULT_ADDON_ADJUSTMENT: AddonScalarAdjustment = {
  adjustment_type: OptimizationAddonAdjustmentType.INCREASE_BY_PERCENTAGE,
  adjustment_value: 10,
};

type BaseActionFields = Pick<OptimizationAction, 'display_name' | 'condition'>;

const getBaseActionFields = (action: BaseActionFields) => ({
  display_name: action.display_name,
  condition: action.condition,
});

const isUniformAdjustment = <
  T extends { adjustment_type: string; adjustment_value: number },
>(
  adjustments: T[] | undefined
) => {
  if (!adjustments?.length) {
    return undefined;
  }

  const [first] = adjustments;
  if (
    adjustments.every(
      (adjustment) =>
        adjustment.adjustment_type === first?.adjustment_type &&
        adjustment.adjustment_value === first.adjustment_value
    )
  ) {
    return first;
  }

  return undefined;
};

export const isPlanPercentageAdjustmentType = (
  adjustmentType: string | undefined
) =>
  adjustmentType === OptimizationPriceAdjustmentType.INCREASE_BY_PERCENTAGE ||
  adjustmentType === OptimizationPriceAdjustmentType.DECREASE_BY_PERCENTAGE;

export const isAddonPercentageAdjustmentType = (
  adjustmentType: string | undefined
) =>
  adjustmentType === OptimizationAddonAdjustmentType.INCREASE_BY_PERCENTAGE ||
  adjustmentType === OptimizationAddonAdjustmentType.DECREASE_BY_PERCENTAGE;

export const getPlanScopeForUi = (
  action: PlanPriceAdjustmentAction
): OptimizationPlanScope => OptimizationSliceHelper.getPlanScope(action);

export const getAddonScopeForUi = (
  action: AddonPriceAdjustmentAction
): OptimizationAddonScope => OptimizationSliceHelper.getAddonScope(action);

const getAllPlansScalarAdjustment = (
  action: PlanPriceAdjustmentAction
): PlanScalarAdjustment | undefined =>
  action.adjustment_type !== undefined && action.adjustment_value !== undefined
    ? {
        adjustment_type: action.adjustment_type,
        adjustment_value: action.adjustment_value,
      }
    : undefined;

const getSpecificPlansScalarAdjustment = (
  action: PlanPriceAdjustmentAction
): PlanScalarAdjustment | undefined => {
  const uniform = isUniformAdjustment(action.plan_adjustments);
  if (!uniform) {
    return undefined;
  }

  return {
    adjustment_type: uniform.adjustment_type,
    adjustment_value: uniform.adjustment_value,
  };
};

const getPlanScalarAdjustmentForCarry = (
  action: PlanPriceAdjustmentAction
): PlanScalarAdjustment | undefined =>
  getPlanScopeForUi(action) === OptimizationPlanScope.ALL_PLANS
    ? getAllPlansScalarAdjustment(action)
    : getSpecificPlansScalarAdjustment(action);

const getAllAddonsScalarAdjustment = (
  action: AddonPriceAdjustmentAction
): AddonScalarAdjustment | undefined =>
  action.adjustment_type !== undefined && action.adjustment_value !== undefined
    ? {
        adjustment_type: action.adjustment_type,
        adjustment_value: action.adjustment_value,
      }
    : undefined;

const getSpecificAddonsScalarAdjustment = (
  action: AddonPriceAdjustmentAction
): AddonScalarAdjustment | undefined => {
  const uniform = isUniformAdjustment(action.addons);
  if (!uniform) {
    return undefined;
  }

  return {
    adjustment_type: uniform.adjustment_type,
    adjustment_value: uniform.adjustment_value,
  };
};

const getAddonScalarAdjustmentForCarry = (
  action: AddonPriceAdjustmentAction
): AddonScalarAdjustment | undefined =>
  getAddonScopeForUi(action) === OptimizationAddonScope.ALL_ADDONS
    ? getAllAddonsScalarAdjustment(action)
    : getSpecificAddonsScalarAdjustment(action);

const toAddonScalarAdjustment = (
  planScalar: PlanScalarAdjustment | undefined
): AddonScalarAdjustment | undefined => {
  if (!planScalar) {
    return undefined;
  }
  if (
    planScalar.adjustment_type ===
    OptimizationPriceAdjustmentType.SET_FIXED_PRICE
  ) {
    return undefined;
  }

  return {
    adjustment_type:
      planScalar.adjustment_type as unknown as OptimizationAddonAdjustmentType,
    adjustment_value: planScalar.adjustment_value,
  };
};

const toPlanScalarAdjustment = (
  addonScalar: AddonScalarAdjustment | undefined
): PlanScalarAdjustment | undefined => {
  if (!addonScalar) {
    return undefined;
  }

  return {
    adjustment_type:
      addonScalar.adjustment_type as unknown as OptimizationPriceAdjustmentType,
    adjustment_value: addonScalar.adjustment_value,
  };
};

const createAllPlansAction = (
  action: BaseActionFields,
  scalar?: PlanScalarAdjustment
): OptimizationAction => ({
  ...getBaseActionFields(action),
  type: OptimizationActionType.PLAN_PRICE_ADJUSTMENT,
  scope: OptimizationPlanScope.ALL_PLANS,
  adjustment_type:
    scalar?.adjustment_type ?? DEFAULT_PLAN_ADJUSTMENT.adjustment_type,
  adjustment_value:
    scalar?.adjustment_value ?? DEFAULT_PLAN_ADJUSTMENT.adjustment_value,
});

const createSpecificPlansAction = (
  action: BaseActionFields,
  scalar?: PlanScalarAdjustment
): OptimizationAction => ({
  ...getBaseActionFields(action),
  type: OptimizationActionType.PLAN_PRICE_ADJUSTMENT,
  scope: OptimizationPlanScope.SPECIFIC_PLANS,
  plan_adjustments: [
    {
      ...OptimizationSliceHelper.createNewPlanAdjustment(),
      adjustment_type:
        scalar?.adjustment_type ?? DEFAULT_PLAN_ADJUSTMENT.adjustment_type,
      adjustment_value:
        scalar?.adjustment_value ?? DEFAULT_PLAN_ADJUSTMENT.adjustment_value,
    },
  ],
});

const createPlanChangeAction = (
  action: BaseActionFields
): OptimizationAction => ({
  ...getBaseActionFields(action),
  type: OptimizationActionType.PLAN_CHANGE,
  plans: [OptimizationSliceHelper.createNewPlanChangePlan()],
});

const createAllAddonsAction = (
  action: BaseActionFields,
  scalar?: AddonScalarAdjustment
): OptimizationAction => ({
  ...getBaseActionFields(action),
  type: OptimizationActionType.ADDON_PRICE_ADJUSTMENT,
  scope: OptimizationAddonScope.ALL_ADDONS,
  adjustment_type:
    scalar?.adjustment_type ?? DEFAULT_ADDON_ADJUSTMENT.adjustment_type,
  adjustment_value:
    scalar?.adjustment_value ?? DEFAULT_ADDON_ADJUSTMENT.adjustment_value,
});

const createSpecificAddonsAction = (
  action: BaseActionFields,
  scalar?: AddonScalarAdjustment
): OptimizationAction => ({
  ...getBaseActionFields(action),
  type: OptimizationActionType.ADDON_PRICE_ADJUSTMENT,
  scope: OptimizationAddonScope.SPECIFIC_ADDONS,
  addons: [
    {
      ...OptimizationSliceHelper.createNewAddonAdjustment(),
      adjustment_type:
        scalar?.adjustment_type ?? DEFAULT_ADDON_ADJUSTMENT.adjustment_type,
      adjustment_value:
        scalar?.adjustment_value ?? DEFAULT_ADDON_ADJUSTMENT.adjustment_value,
    },
  ],
});

export const createActionForType = (
  action: OptimizationAction,
  nextType: OptimizationActionType
): OptimizationAction => {
  switch (nextType) {
    case OptimizationActionType.PLAN_PRICE_ADJUSTMENT:
      return createAllPlansAction(action);
    case OptimizationActionType.PLAN_CHANGE:
      return createPlanChangeAction(action);
    case OptimizationActionType.ADDON_PRICE_ADJUSTMENT:
      return createAllAddonsAction(action);
    default:
      return action;
  }
};

export const createPlanScopeSwitchChange = (
  action: PlanPriceAdjustmentAction,
  nextScope: OptimizationPlanScope
): PendingActionChange => {
  if (nextScope === OptimizationPlanScope.SPECIFIC_PLANS) {
    return {
      nextAction: createSpecificPlansAction(
        action,
        getAllPlansScalarAdjustment(action)
      ),
      prompt: {
        title: 'Switch Plan Scope?',
        description:
          'Rule name and condition will be kept. The current adjustment type and value will move into the first specific-plan row. You will need to choose specific plans for the new scope.',
        confirmLabel: 'Continue',
      },
    };
  }

  const sharedScalar = getSpecificPlansScalarAdjustment(action);
  return {
    nextAction: createAllPlansAction(action, sharedScalar),
    prompt: {
      title: 'Switch Plan Scope?',
      description: sharedScalar
        ? 'Rule name and condition will be kept. The selected plans will be cleared, and the shared adjustment type and value will be kept as an all-plans rule.'
        : 'Rule name and condition will be kept. The selected plans and per-plan adjustments will be cleared, and the action will reset to the default all-plans adjustment.',
      confirmLabel: 'Continue',
    },
  };
};

export const createActionTypeSwitchChange = (
  action: OptimizationAction,
  nextType: OptimizationActionType
): PendingActionChange => {
  if (nextType === OptimizationActionType.PLAN_CHANGE) {
    let description =
      'Rule name and condition will be kept. Pricing configuration will be cleared and replaced with a new plan change row.';

    if (action.type === OptimizationActionType.PLAN_CHANGE) {
      description =
        'Rule name and condition will be kept. The current and target plan mappings will be reset.';
    }

    return {
      nextAction: createPlanChangeAction(action),
      prompt: {
        title: 'Switch Action Type?',
        description,
        confirmLabel: 'Continue',
      },
    };
  }

  if (nextType === OptimizationActionType.PLAN_PRICE_ADJUSTMENT) {
    const carriedScalar =
      action.type === OptimizationActionType.ADDON_PRICE_ADJUSTMENT
        ? toPlanScalarAdjustment(
            getAddonScalarAdjustmentForCarry(
              action as AddonPriceAdjustmentAction
            )
          )
        : undefined;

    const description =
      action.type === OptimizationActionType.ADDON_PRICE_ADJUSTMENT
        ? carriedScalar
          ? 'Rule name and condition will be kept. The selected addons will be cleared, and the shared adjustment type and value will be kept as an all-plans rule.'
          : 'Rule name and condition will be kept. Addon scope, selected addons, and addon pricing configuration will be cleared. The action will reset to the default all-plans adjustment.'
        : 'Rule name and condition will be kept. Current and target plan mappings will be cleared, and the action will reset to the default all-plans adjustment.';

    return {
      nextAction: createAllPlansAction(action, carriedScalar),
      prompt: {
        title: 'Switch Action Type?',
        description,
        confirmLabel: 'Continue',
      },
    };
  }

  const carriedScalar =
    action.type === OptimizationActionType.PLAN_PRICE_ADJUSTMENT
      ? toAddonScalarAdjustment(
          getPlanScalarAdjustmentForCarry(action as PlanPriceAdjustmentAction)
        )
      : undefined;

  const description =
    action.type === OptimizationActionType.PLAN_PRICE_ADJUSTMENT
      ? carriedScalar
        ? 'Rule name and condition will be kept. Plan scope and selected plans will be cleared, and the shared adjustment type and value will be kept as an all-addons rule.'
        : 'Rule name and condition will be kept. Plan scope, selected plans, and plan pricing configuration will be cleared. The action will reset to the default all-addons adjustment.'
      : 'Rule name and condition will be kept. Current and target plan mappings will be cleared, and the action will reset to the default all-addons adjustment.';

  return {
    nextAction: createAllAddonsAction(action, carriedScalar),
    prompt: {
      title: 'Switch Action Type?',
      description,
      confirmLabel: 'Continue',
    },
  };
};

export const createAddonScopeSwitchAction = (
  action: AddonPriceAdjustmentAction,
  nextScope: OptimizationAddonScope
): OptimizationAction =>
  nextScope === OptimizationAddonScope.ALL_ADDONS
    ? createAllAddonsAction(action, getSpecificAddonsScalarAdjustment(action))
    : createSpecificAddonsAction(action, getAllAddonsScalarAdjustment(action));
